import {
  FileMessagePart as FileContentPart,
  ReasoningMessagePart as ReasoningContentPart,
  SourceMessagePart as SourceContentPart,
  TextMessagePart as TextContentPart,
  ToolCallMessagePart as ToolCallContentPart,
} from "@assistant-ui/react";
import type { ReadonlyJSONObject } from "../jsonTypes";
import { FrontPosition, FrontPositionSchema } from "../../event";
import { TToolCallBackendData } from "../../RuntimeProvider";

export type ThreadAssistantContentPart =
  | TextContentPart
  | ReasoningContentPart
  | ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>
  | SourceContentPart
  | FileContentPart;

export type ToolCallsRecord = Record<
  string,
  ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>
>;

/**
 * Repositions tools marked "at_end" to the end of the content
 * @param content - Current array of content parts to reposition
 * @returns Rearranged content array with at_end tools at the end
 */
export function repositionEndTools(
  content: ThreadAssistantContentPart[],
): ThreadAssistantContentPart[] {
  const { toolsAtEnd, otherContent } = separateEndTools(content);

  if (toolsAtEnd.length > 0) {
    return [...otherContent, ...toolsAtEnd];
  }

  return content;
}

/**
 * Separates tools marked "at_end" from other content
 * @param content - Array of content parts to separate
 * @returns Object containing two arrays: toolsAtEnd and otherContent
 */
export function separateEndTools(content: ThreadAssistantContentPart[]): {
  toolsAtEnd: ThreadAssistantContentPart[];
  otherContent: ThreadAssistantContentPart[];
} {
  return content.reduce(
    (acc, part) => {
      if (isAtEndTool(part)) {
        acc.toolsAtEnd.push(part);
      } else {
        acc.otherContent.push(part);
      }
      return acc;
    },
    {
      toolsAtEnd: [] as ThreadAssistantContentPart[],
      otherContent: [] as ThreadAssistantContentPart[],
    },
  );
}

/**
 * Checks if a part is a tool with "at_end" position
 * @param part - Content part to check
 * @returns Boolean indicating if the part is a tool with at_end position
 */
export function isAtEndTool(part: ThreadAssistantContentPart): boolean {
  if (part.type !== "tool-call") return false;

  const toolCallPart = part as ToolCallContentPart<
    ReadonlyJSONObject,
    TToolCallBackendData
  >;
  if (!toolCallPart.result) return false;

  const toolData = toolCallPart.result as TToolCallBackendData;
  return toolData.front_position === FrontPositionSchema.enum.at_end;
}

/**
 * Factory for tool positioning strategies
 * @param filteredContent - Content array without the tool
 * @param toolCall - Tool call content part to position
 * @returns Function that implements the positioning strategy
 */
export const positioningStrategies: Record<
  FrontPosition | string,
  (
    filteredContent: ThreadAssistantContentPart[],
    toolCall: ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>,
  ) => ThreadAssistantContentPart[]
> = {
  at_start: (filteredContent, toolCall) => {
    return [toolCall, ...filteredContent];
  },

  at_end: (filteredContent, toolCall) => {
    const { toolsAtEnd, otherContent } = separateEndTools(filteredContent);
    return [...otherContent, ...toolsAtEnd, toolCall];
  },

  in_conversation_tool_start: (filteredContent, toolCall) => {
    const lastTextIndex = findLastTextIndex(filteredContent);
    return [
      ...filteredContent.slice(0, lastTextIndex + 1),
      toolCall,
      ...filteredContent.slice(lastTextIndex + 1),
    ];
  },

  in_conversation_tool_end: (filteredContent, toolCall) => {
    const parts = separateContentParts(filteredContent);

    if (parts.textContent.length > 0) {
      return [
        ...parts.textContent,
        toolCall,
        ...parts.otherToolsContent,
        ...parts.toolsAtEnd,
      ];
    } else {
      return [toolCall, ...parts.otherToolsContent, ...parts.toolsAtEnd];
    }
  },

  // Default strategy
  default: (filteredContent, toolCall) => {
    const lastTextIndex = findLastTextIndex(filteredContent);
    return [
      ...filteredContent.slice(0, lastTextIndex + 1),
      toolCall,
      ...filteredContent.slice(lastTextIndex + 1),
    ];
  },
};

/**
 * Updates a tool's position in the content array
 * @param content - Current array of content parts
 * @param toolCallId - ID of the tool call to reposition
 * @param toolCall - The tool call content part to be repositioned
 * @param newPosition - The new position designation for the tool
 * @returns Updated content array with the tool in its new position
 */
export function updateToolPositionInContent(
  content: ThreadAssistantContentPart[],
  toolCallId: string,
  toolCall: ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>,
  newPosition: FrontPosition,
): ThreadAssistantContentPart[] {
  const filteredContent = removeToolFromContent(content, toolCallId);

  // Get the appropriate positioning strategy
  const positionStrategy =
    positioningStrategies[newPosition] || positioningStrategies.default;

  // Apply the strategy
  return positionStrategy(filteredContent, toolCall);
}

/**
 * Updates a tool in place without changing its position
 * @param content - Current array of content parts
 * @param toolCallId - ID of the tool call to update
 * @param updatedToolCall - New tool call content to replace the existing one
 * @returns Updated content array with the tool replaced in-place
 */
export function updateToolInPlace(
  content: ThreadAssistantContentPart[],
  toolCallId: string,
  updatedToolCall: ToolCallContentPart<
    ReadonlyJSONObject,
    TToolCallBackendData
  >,
): ThreadAssistantContentPart[] {
  return content.map((part) =>
    part.type === "tool-call" &&
    (part as ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>)
      .toolCallId === toolCallId
      ? updatedToolCall
      : part,
  );
}

/**
 * Removes a tool from the content
 * @param content - Current array of content parts
 * @param toolCallId - ID of the tool call to remove
 * @returns Content array with the specified tool removed
 */
export function removeToolFromContent(
  content: ThreadAssistantContentPart[],
  toolCallId: string,
): ThreadAssistantContentPart[] {
  return content.filter(
    (part) =>
      part.type !== "tool-call" ||
      (part as ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>)
        .toolCallId !== toolCallId,
  );
}

/**
 * Finds the index of the last text part in the content
 * @param content - Array of content parts to search
 * @returns Index of the last text part or -1 if none found
 */
export function findLastTextIndex(
  content: ThreadAssistantContentPart[],
): number {
  const lastTextIndex = content.findIndex((part) => part.type === "text");
  return lastTextIndex !== -1 ? lastTextIndex : -1;
}

/**
 * Separates content into text parts, "at_end" tools, and other tools
 * @param content - Array of content parts to separate
 * @returns Object containing three arrays: textContent, toolsAtEnd, and otherToolsContent
 */
export function separateContentParts(content: ThreadAssistantContentPart[]): {
  textContent: ThreadAssistantContentPart[];
  toolsAtEnd: ThreadAssistantContentPart[];
  otherToolsContent: ThreadAssistantContentPart[];
} {
  return content.reduce(
    (acc, part) => {
      if (part.type === "text") {
        acc.textContent.push(part);
      } else if (isAtEndTool(part)) {
        acc.toolsAtEnd.push(part);
      } else {
        acc.otherToolsContent.push(part);
      }
      return acc;
    },
    {
      textContent: [] as ThreadAssistantContentPart[],
      toolsAtEnd: [] as ThreadAssistantContentPart[],
      otherToolsContent: [] as ThreadAssistantContentPart[],
    },
  );
}

/**
 * Updates existing text content or creates a new one
 * @param content - Current array of content parts
 * @param text - New text to be added
 * @param currentText - Current accumulated text including the new text
 * @returns Updated content array with the text either added to existing part or as new part
 */
export function updateTextContent(
  content: ThreadAssistantContentPart[],
  text: string,
  currentText: string,
): ThreadAssistantContentPart[] {
  if (content.length === 0) {
    return createNewTextPart(content, currentText);
  }

  const lastElement = content[content.length - 1];

  // Case 1: Last element is text - append to the text
  if (lastElement.type === "text") {
    return updateExistingTextPart(content, content.length - 1, text);
  }

  // Case 2: Last element is a tool
  if (lastElement.type === "tool-call") {
    const toolCall = lastElement as ToolCallContentPart<
      ReadonlyJSONObject,
      TToolCallBackendData
    >;
    const toolData = toolCall.result as TToolCallBackendData;
    const frontPosition = toolData?.front_position;

    // If it's an at_end tool, append to the last existing text
    if (frontPosition === FrontPositionSchema.enum.at_end) {
      const textParts = content.filter((part) => part.type === "text");
      const lastTextPartIndex =
        textParts.length > 0
          ? content.lastIndexOf(textParts[textParts.length - 1])
          : -1;

      if (lastTextPartIndex !== -1) {
        return updateExistingTextPart(content, lastTextPartIndex, text);
      } else {
        return createNewTextPart(content, currentText);
      }
    }

    // If it's a tool with in_conversation_tool_start, in_conversation_tool_end or at_start
    // create a new text element
    if (
      frontPosition === FrontPositionSchema.enum.in_conversation_tool_start ||
      frontPosition === FrontPositionSchema.enum.in_conversation_tool_end ||
      frontPosition === FrontPositionSchema.enum.at_start
    ) {
      return createNewTextPart(content, currentText);
    }
  }

  // Default case: create a new text element
  return createNewTextPart(content, currentText);
}

/**
 * Updates an existing text part
 * @param content - Current array of content parts
 * @param lastTextPartIndex - Index of the last text part in the content array
 * @param text - New text to be appended to the existing text part
 * @returns New content array with the updated text part
 */
export function updateExistingTextPart(
  content: ThreadAssistantContentPart[],
  lastTextPartIndex: number,
  text: string,
): ThreadAssistantContentPart[] {
  const lastTextPart = content[lastTextPartIndex] as TextContentPart;
  const updatedTextPart = {
    ...lastTextPart,
    text: lastTextPart.text + text,
  };

  return [
    ...content.slice(0, lastTextPartIndex),
    updatedTextPart,
    ...content.slice(lastTextPartIndex + 1),
  ];
}

/**
 * Creates a new text part
 * @param content - Current array of content parts
 * @param currentText - Text to include in the new text part
 * @returns Updated content array with a new text part added
 */
export function createNewTextPart(
  content: ThreadAssistantContentPart[],
  currentText: string,
): ThreadAssistantContentPart[] {
  return [...content, { type: "text", text: currentText }];
}

/**
 * Appends new text to the current text
 * @param currentText - The existing text buffer
 * @param newText - The new text to append
 * @returns Combined text string
 */
export function appendTextToCurrentText(
  currentText: string,
  newText: string,
): string {
  return currentText + newText;
}

/**
 * Adds a dot to the last text content part in the results array
 * @param result - Array of content parts to modify
 */
function addDotToLastTextContent(result: ThreadAssistantContentPart[]): void {
  // Find the last text content part from the end
  for (let i = result.length - 1; i >= 0; i--) {
    const part = result[i];
    if (part.type === "text") {
      // Replace the text content part with a new one that includes the dot (trim to remove trailing spaces)
      result[i] = { type: "text", text: part.text.trimEnd() + "." };
      break;
    }
  }
}

/**
 * Processes text content to handle dot removal rules
 * @param text - The text content to process
 * @returns The processed text or null if the element should be removed
 */
function processTextContent(text: string): string | null {
  const trimmedText = text.trim();

  // If content contains only a dot, remove the entire element
  if (trimmedText === ".") {
    return null;
  }

  // If content starts with a dot, remove only the dot
  if (trimmedText.startsWith(".") && trimmedText.length > 1) {
    const withoutDot = trimmedText.substring(1);
    // Preserve original whitespace structure but replace content
    return text.replace(trimmedText, withoutDot);
  }

  return text;
}

/**
 * Inserts a tool call by finding and replacing the original text in the last text element
 * @param content - Current array of content parts
 * @param originalText - The exact text to find and replace
 * @param toolCall - The tool call to insert
 * @returns Updated content array with the tool inserted at the correct position
 */
export function insertToolAtPosition(
  content: ThreadAssistantContentPart[],
  originalText: string,
  toolCall: ToolCallContentPart<ReadonlyJSONObject, TToolCallBackendData>,
): ThreadAssistantContentPart[] {
  // Find the last text element
  let lastTextIndex = -1;
  for (let i = content.length - 1; i >= 0; i--) {
    if (content[i].type === "text") {
      lastTextIndex = i;
      break;
    }
  }

  if (lastTextIndex === -1) {
    // No text found, just add the tool at the end
    return [...content, toolCall];
  }

  const lastTextPart = content[lastTextIndex] as TextContentPart;
  const lastOccurrenceIndex = lastTextPart.text.lastIndexOf(originalText);

  if (lastOccurrenceIndex === -1) {
    // Original text not found, just add the tool at the end
    return [...content, toolCall];
  }

  // Check if we need to consume a dot after the original text
  const shouldConsumeDot = lastTextPart.text
    .slice(lastOccurrenceIndex + originalText.length)
    .trimStart()
    .startsWith(".");

  const actualLength = shouldConsumeDot
    ? originalText.length + 1
    : originalText.length;

  // Split the text at the last occurrence
  const beforeText = lastTextPart.text.slice(0, lastOccurrenceIndex);
  const afterText = lastTextPart.text.slice(lastOccurrenceIndex + actualLength);

  // Build the new content
  const result: ThreadAssistantContentPart[] = [
    ...content.slice(0, lastTextIndex), // All content before the last text
  ];

  // Add the text before the replacement (if any)
  if (beforeText) {
    const processedBeforeText = processTextContent(beforeText);
    if (processedBeforeText !== null) {
      result.push({
        type: "text",
        text: processedBeforeText,
      });
    }
  }

  // Add the tool call
  result.push(toolCall);

  // If we consumed a dot, add it to the last text content
  if (shouldConsumeDot) {
    addDotToLastTextContent(result);
  }

  // Add the text after the replacement (if any)
  if (afterText) {
    const processedAfterText = processTextContent(afterText);
    if (processedAfterText !== null) {
      result.push({
        type: "text",
        text: processedAfterText,
      });
    }
  }

  // Add any content that was after the last text element
  result.push(...content.slice(lastTextIndex + 1));

  // Check if we need to add an empty text element after the tool
  // This is needed if there's no text content after the tool call for streaming to continue
  const hasTextAfterTool = result.some((part, index) => {
    const toolIndex = result.findIndex((p) => p === toolCall);
    return index > toolIndex && part.type === "text";
  });

  if (!hasTextAfterTool) {
    result.push({
      type: "text",
      text: "",
    });
  }

  return result;
}
